import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import type { Database } from '@/types/database'

type AccessRole = Database['public']['Enums']['access_role']

// Only what the admin team-access surfaces render — not select('*').
const USER_COLUMNS = 'id, full_name, email, role, is_active'

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        .select(USER_COLUMNS)
        .order('full_name')
      if (error) throw error
      return data
    },
    staleTime: 60_000,
  })
}

export function useAccountAccess(accountId: string) {
  return useQuery({
    queryKey: ['account-access', accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_user_access')
        .select(
          'id, account_id, user_id, access_role, granted_at, user:users(id,full_name,email,role), granter:granted_by(full_name)',
        )
        .eq('account_id', accountId)
        .order('granted_at')
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })
}

export function useUserAccess(userId: string) {
  return useQuery({
    queryKey: ['user-access', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_user_access')
        .select('id, account_id, access_role, account:accounts(id,name,type)')
        .eq('user_id', userId)
        .order('granted_at')
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })
}

export function useGrantAccess() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['team-access', 'grant'],
    mutationFn: async ({
      accountId,
      userId,
      role,
    }: {
      accountId: string
      userId: string
      role: AccessRole
    }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      const { error } = await supabase.from('account_user_access').insert({
        account_id: accountId,
        user_id: userId,
        access_role: role,
        granted_by: user?.id ?? null,
      })
      if (error) throw error
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['user-access', vars.userId] })
      void qc.invalidateQueries({ queryKey: ['account-access', vars.accountId] })
      toast.success('Access granted')
    },
    onError: (err: Error) => toast.error(err.message),
  })
}

export function useRevokeAccess() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['team-access', 'revoke'],
    mutationFn: async ({ id, userId, accountId }: { id: string; userId: string; accountId: string }) => {
      const { error } = await supabase.from('account_user_access').delete().eq('id', id)
      if (error) throw error
      return { userId, accountId }
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['user-access', vars.userId] })
      void qc.invalidateQueries({ queryKey: ['account-access', vars.accountId] })
      toast.success('Access revoked')
    },
    onError: (err: Error) => toast.error(err.message),
  })
}
